import { useChat } from "@ai-sdk/react";
import type { UIMessage } from "ai";
import { DefaultChatTransport } from "ai";
import config from "config";
import { identity } from "lodash/util";
import { useCallback, useRef } from "react";
import { isNativePlatform } from "src/capacitor/appsHelpers";
import newApi from "src/newApi";

interface UseChatStreamParams {
	getBody: (messages: UIMessage[]) => Record<string, unknown>;
	onFinish?: () => void;
	onError?: () => void;
	promptType: string;
	/**
	 * "text" (domyślnie) — backend streamuje surowy tekst (toTextStreamResponse).
	 * "data" — backend streamuje standardowy Vercel AI SDK UI Message Stream
	 * (pipeUIMessageStreamToResponse, zdarzenia text-start/text-delta/text-end).
	 */
	streamProtocol?: "text" | "data";
}

export const useChatStream = ({
	getBody,
	onFinish = identity,
	onError = identity,
	promptType,
	streamProtocol = "text",
}: UseChatStreamParams) => {
	const isRegenerateRef = useRef(false);

	const { messages, sendMessage, status, setMessages } = useChat({
		onFinish,
		onError,
		transport: new DefaultChatTransport({
			prepareSendMessagesRequest: ({ messages }) => {
				const api = isRegenerateRef.current
					? newApi.getAiResponseRegenerateStreamUrl(promptType)
					: newApi.getAiResponseStreamUrl(promptType);
				isRegenerateRef.current = false;
				return {
					api,
					body: getBody(messages),
					headers: {
						...(config.CF_AUTH_CLIENT_ID && isNativePlatform()
							? { "CF-Access-Client-Id": config.CF_AUTH_CLIENT_ID }
							: {}),
						...(config.CF_AUTH_CLIENT_SECRET && isNativePlatform()
							? { "CF-Access-Client-Secret": config.CF_AUTH_CLIENT_SECRET }
							: {}),
					},
					streamProtocol,
					credentials: "include" as const,
					mode: "cors" as const,
				};
			},
		}),
	});

	const regenerate = useCallback(
		(...args: Parameters<typeof sendMessage>) => {
			isRegenerateRef.current = true;
			return sendMessage(...args);
		},
		[sendMessage],
	);

	const isStreaming = ["streaming"].includes(status);
	const isSubmitted = ["submitted"].includes(status);

	// Status zmienia się na "streaming" już przy zdarzeniu text-start, czyli
	// zanim dotrze pierwszy token. Między text-start a pierwszym text-delta
	// potrafi być zauważalna przerwa — trzymamy loader dopóki nie pojawi się
	// realna treść odpowiedzi.
	const lastMessage = messages[messages.length - 1];
	const streamedText =
		lastMessage?.role === "assistant"
			? lastMessage.parts
					.map((part) => (part.type === "text" ? part.text : ""))
					.join("")
			: "";
	const isWaitingForFirstToken = isStreaming && streamedText.length === 0;

	return {
		messages,
		sendMessage,
		regenerate,
		status,
		setMessages,
		isStreaming,
		isSubmitted,
		isWaitingForFirstToken,
	};
};
